import { trackDesignSystemAction, trackLearningAction } from 'analytics';
import forge from 'apis/forge';
import SidebarToggle from 'components/Widget/Button/SidebarToggle';
import LoadingOverlay from 'components/Widget/Overlay/LoadingOverlay';
import { CreateSceneChatResponse, CreateSceneResponse } from 'dragonmouse-shared/api/forge';
import { Attachment } from 'dragonmouse-shared/interfaces';
import { useEffect, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import {
	selectDesignSystems,
	selectOrganizations,
	selectUser,
	selectUserSettings,
} from 'redux/db/reducers';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import * as routes from 'routes';
import CreatorInput from '../CreatorInput';
import * as Base from '../styles';

const ProjectCreator = () => {
	const navigate = useNavigate();
	// ProjectCreator has its own loader. Like the projects page, it does some
	// things without optimistic updates. When it does this, it shows a semi-transparent loading
	// which is different from the main App's loading indicator (which is opaque).
	// [perhaps we should generalize the main app's loader - ss]
	//
	// We use the existence of a loading message to gate the display of the loader
	const [loadingMessage, setLoadingMessage] = useState<string>('');
	const {
		isMobile,
		organizations,
		defaultOrganizationId,
		defaultDesignSystemId,
		designSystems,
		user,
	} = useSelector(
		(r: RootReducer) => ({
			isMobile: selectIsMobile(r.mode),
			organizations: selectOrganizations(r.db),
			defaultOrganizationId: selectUserSettings(r.db).defaultOrganizationId,
			defaultDesignSystemId: selectUserSettings(r.db).defaultDesignSystemId,
			designSystems: selectDesignSystems(r.db),
			user: selectUser(r.db),
		}),
		shallowEqual
	);

	const [selectedDesignSystemId, setSelectedDesignSystemId] = useState<string | null>(
		defaultDesignSystemId ?? null
	);
	const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(
		defaultOrganizationId ?? null
	);
	const prevDefaultOrgRef = useRef<string | null | undefined>(undefined);

	// Sync local state when default org changes (profile load or user changed it in Settings)
	useEffect(() => {
		if (prevDefaultOrgRef.current !== defaultOrganizationId) {
			prevDefaultOrgRef.current = defaultOrganizationId;
			setSelectedOrganizationId(defaultOrganizationId ?? null);
		}
	}, [defaultOrganizationId]);

	// Validate that default org still exists; clear if deleted
	useEffect(() => {
		if (!defaultOrganizationId || !user || user.id === 'guest') return;
		const orgExists = organizations.some((org) => org.id === defaultOrganizationId);
		if (!orgExists) {
			setSelectedOrganizationId(null);
			forge.updateUser({
				update: {
					id: user.id,
					metadata: {
						settings: { ...user.metadata?.settings, defaultOrganizationId: null },
					},
				},
			});
		}
	}, [organizations, defaultOrganizationId, user]);

	// Sync local state when user profile loads with a default DS
	useEffect(() => {
		if (defaultDesignSystemId && !selectedDesignSystemId) {
			setSelectedDesignSystemId(defaultDesignSystemId);
		}
	}, [defaultDesignSystemId]);

	// Validate that default DS still exists; clear if deleted
	useEffect(() => {
		if (defaultDesignSystemId && designSystems.length > 0) {
			const dsExists = designSystems.some((ds) => ds.id === defaultDesignSystemId);
			if (!dsExists && user && user.id !== 'guest') {
				forge.updateUser({
					update: {
						id: user.id,
						metadata: {
							settings: { ...user.metadata?.settings, defaultDesignSystemId: null },
						},
					},
				});
			}
		}
	}, [designSystems, defaultDesignSystemId, user]);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onNewProject = async (
		prompt: string,
		attachments: Attachment[],
		designSystemId?: string | null,
		designSystemVersionNumber?: number
	) => {
		try {
			setLoadingMessage('creating project...');
			trackLearningAction('onboarding_prompt_input', 'complete');
			const effectiveDsId = designSystemId ?? selectedDesignSystemId;
			if (effectiveDsId) trackDesignSystemAction('create_scene_with_ds', { dsId: effectiveDsId });
			const params = {
				prompt,
				attachments,
				...(effectiveDsId && { designSystemId: effectiveDsId }),
				...(effectiveDsId && designSystemVersionNumber && { designSystemVersionNumber }),
				...(selectedOrganizationId && { organizationId: selectedOrganizationId }),
			};
			const res: CreateSceneChatResponse = await forge.createSceneChatFromPrompt.withToast()(
				params
			);
			setLoadingMessage('');
			navigate(routes.scene(res.scene.id, res.scene.title));
		} catch (e) {
			setLoadingMessage('');
		}
	};

	const onDesignSystemSelect = (designSystemId: string | null) => {
		setSelectedDesignSystemId(designSystemId);
	};

	const onOwnershipSelect = (organizationId: string | null) => {
		setSelectedOrganizationId(organizationId);
	};

	const onCreateEmptyProject = async (
		designSystemId?: string | null,
		designSystemVersionNumber?: number
	) => {
		try {
			setLoadingMessage('creating project...');
			const effectiveDsId = designSystemId ?? selectedDesignSystemId;
			if (effectiveDsId) trackDesignSystemAction('create_scene_with_ds', { dsId: effectiveDsId });
			const res: CreateSceneResponse = await forge.createScene.withToast()({
				title: 'My Project',
				...(effectiveDsId && { designSystemId: effectiveDsId }),
				...(effectiveDsId && designSystemVersionNumber && { designSystemVersionNumber }),
				...(selectedOrganizationId && { organizationId: selectedOrganizationId }),
			});
			setLoadingMessage('');
			navigate(routes.scene(res.sceneId, 'My Project'));
		} catch (e) {
			setLoadingMessage('');
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	return (
		<Base.Base>
			{isMobile && (
				<Base.SidebarToggle>
					<SidebarToggle />
				</Base.SidebarToggle>
			)}
			{loadingMessage && <LoadingOverlay text={loadingMessage} semiTransparent />}
			<Base.Content>
				<Base.Input>
					<Base.Title>What would you like to design&nbsp;today?</Base.Title>
					<CreatorInput
						onSubmit={onNewProject}
						disabled={!!loadingMessage}
						mode="project"
						onDesignSystemSelect={onDesignSystemSelect}
						initialDesignSystemId={defaultDesignSystemId}
						onCreateBlank={onCreateEmptyProject}
						organizationId={selectedOrganizationId}
						organizations={organizations}
						onOwnershipSelect={onOwnershipSelect}
					/>
				</Base.Input>
			</Base.Content>
		</Base.Base>
	);
};

export default ProjectCreator;
